import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  BookOpen,
  CircleCheck,
  Clock,
  ListOrdered,
  Loader2,
  Search,
  Store,
  Utensils,
} from "lucide-react";
import { Link } from "react-router-dom";
import ReactSelect, { SingleValue } from "react-select";
import { PainelProvider, usePainel } from "./context/PainelContext";
import { PainelKanban } from "./components/PainelKanban";
import { BordasEmFaltaModal } from "./components/BordasEmFaltaModal";
import { PedidosAtrasadosModal } from "./components/PedidosAtrasadosModal";
import { SELECT_STYLES } from "../../components/DateFilters/constants";
import {
  contarPedidosAtrasadosUnicos,
  listarPedidosAtrasadosPainel,
} from "./utils/pedidosAtrasadosPainel";
import {
  matchesOrigemFiltro,
  resolveCanalOrigem,
} from "./utils/origemPainel";
import { agruparFinalizadosPorPedido } from "./utils/agruparPedidosPainel";

interface LojaOption {
  value: number;
  label: string;
}

const PainelContent: React.FC = () => {
  const {
    data,
    loading,
    refreshing,
    isOffline,
    fetchPainel,
    lojas,
    selectedLojaId,
    setSelectedLojaId,
    showLojaSelector,
    canPriorizarMontagem,
    prioridadesMontagem,
    priorizandoPrioridadeKey,
    priorizarMontagem,
    removendoPrioridadeKey,
    removerPrioridadeMontagem,
  } = usePainel();

  const lojaOptions = useMemo<LojaOption[]>(
    () => lojas.map((loja) => ({ value: loja.id, label: loja.nome })),
    [lojas]
  );

  const selectedLojaOption =
    lojaOptions.find((o) => o.value === selectedLojaId) ?? null;

  const [pedidoBusca, setPedidoBusca] = useState("");
  const [origemFiltro, setOrigemFiltro] = useState<
    "todos" | "salao" | "cardapio"
  >("todos");
  const [bordasModalOpen, setBordasModalOpen] = useState(false);
  const [atrasadosModalOpen, setAtrasadosModalOpen] = useState(false);
  const [finalizadosModalOpen, setFinalizadosModalOpen] = useState(false);
  const closeFinalizadosModal = useCallback(
    () => setFinalizadosModalOpen(false),
    [],
  );
  const [nowMs, setNowMs] = useState(() => Date.now());

  const finalizadosCount = useMemo(() => {
    const itens = (data?.finalizados ?? []).filter((i) =>
      matchesOrigemFiltro(origemFiltro, resolveCanalOrigem(i.canalOrigem)),
    );
    return agruparFinalizadosPorPedido(itens).length;
  }, [data?.finalizados, origemFiltro]);

  const aguardandoBorda = useMemo(() => {
    const itens = data?.aguardandoBorda ?? [];
    if (origemFiltro === "todos") return itens;
    return itens.filter((i) => {
      const canal =
        i.tipo === "salao_doce" || i.tipo === "salao_salgada"
          ? "salao"
          : "cardapio";
      return matchesOrigemFiltro(origemFiltro, canal);
    });
  }, [data?.aguardandoBorda, origemFiltro]);
  const bordasEmFaltaCount = aguardandoBorda.length;

  const pedidosAtrasados = useMemo(() => {
    const todos = listarPedidosAtrasadosPainel(data, nowMs);
    if (origemFiltro === "todos") return todos;
    // Atrasados não trazem canal; reconcilia com os itens do painel.
    const salaoIds = new Set<number>();
    const cardapioIds = new Set<number>();
    if (data) {
      for (const i of [
        ...data.aguardandoMontagem,
        ...data.emMontagem,
        ...data.emForno,
      ]) {
        const canal = resolveCanalOrigem(
          "origem" in i ? (i as { origem?: "cardapio" | "salao" }).origem : undefined,
        );
        (canal === "salao" ? salaoIds : cardapioIds).add(i.displayId);
      }
      for (const i of data.finalizados ?? []) {
        const canal = resolveCanalOrigem(i.canalOrigem);
        (canal === "salao" ? salaoIds : cardapioIds).add(i.displayId);
      }
    }
    return todos.filter((p) => {
      if (origemFiltro === "salao") return salaoIds.has(p.displayId);
      // WEB: inclui cardápio e itens sem marca salão (entrega/paozinho)
      return !salaoIds.has(p.displayId);
    });
  }, [data, nowMs, origemFiltro]);
  const atrasadosCount = useMemo(
    () => contarPedidosAtrasadosUnicos(pedidosAtrasados),
    [pedidosAtrasados],
  );

  useEffect(() => {
    setNowMs(Date.now());
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  if (loading) {
    return (
      <main className="flex h-[calc(100vh-4rem)] items-center justify-center">
        <Loader2 size={32} className="animate-spin text-primary" />
      </main>
    );
  }

  return (
    <main className="flex h-[calc(100vh-4rem)] w-full flex-col overflow-hidden">
      <header className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-xl font-bold text-neutral-800">Painel</h1>
          <button
            type="button"
            onClick={() => setAtrasadosModalOpen(true)}
            className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-sm font-semibold shadow-sm focus:outline-none focus:ring-2 focus:ring-primary ${
              atrasadosCount > 0
                ? "border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100"
                : "border-neutral-300 bg-white text-neutral-600 hover:bg-neutral-50"
            }`}
            aria-label={
              atrasadosCount > 0
                ? `${atrasadosCount} pedido(s) acima de 10 minutos`
                : "Nenhum pedido acima de 10 minutos"
            }
            title="Pedidos parados há mais de 10 minutos na etapa atual"
          >
            <Clock size={16} aria-hidden />
            <span>
              {atrasadosCount > 0 ? `10+ min: ${atrasadosCount}` : "10+ min"}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setBordasModalOpen(true)}
            className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-sm font-semibold shadow-sm focus:outline-none focus:ring-2 focus:ring-primary ${
              bordasEmFaltaCount > 0
                ? "border-red-300 bg-red-50 text-red-800 hover:bg-red-100"
                : "border-neutral-300 bg-white text-neutral-600 hover:bg-neutral-50"
            }`}
            aria-label={
              bordasEmFaltaCount > 0
                ? `${bordasEmFaltaCount} pizza(s) aguardando borda`
                : "Nenhuma borda em falta"
            }
            title="Pizzas paradas por falta de borda"
          >
            <AlertTriangle size={16} aria-hidden />
            <span>
              {bordasEmFaltaCount > 0
                ? `Bordas: ${bordasEmFaltaCount}`
                : "Bordas"}
            </span>
          </button>
          <Link
            to="/sequencia-pedidos"
            className="inline-flex items-center gap-1.5 rounded-md border border-neutral-300 bg-white px-2.5 py-1 text-sm font-semibold text-neutral-700 shadow-sm no-underline transition-colors hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-primary"
            title="Ver sequência completa dos pedidos do dia"
          >
            <ListOrdered size={16} aria-hidden />
            <span>Sequência do dia</span>
          </Link>
          <button
            type="button"
            onClick={() => setFinalizadosModalOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-sm font-semibold text-emerald-900 shadow-sm transition-colors hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-primary"
            title="Ver pedidos finalizados do dia"
          >
            <CircleCheck size={16} aria-hidden />
            <span>
              {finalizadosCount > 0
                ? `Finalizados: ${finalizadosCount}`
                : "Finalizados"}
            </span>
          </button>
          <div
            className="ml-1 inline-flex items-center gap-1 rounded-md bg-neutral-50 p-0.5"
            role="group"
            aria-label="Filtrar origem dos pedidos"
          >
            <button
              type="button"
              onClick={() =>
                setOrigemFiltro((prev) =>
                  prev === "salao" ? "todos" : "salao",
                )
              }
              className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-primary ${
                origemFiltro === "salao"
                  ? "bg-emerald-600 text-white shadow-sm"
                  : "text-neutral-600 hover:bg-white hover:text-neutral-900"
              }`}
              aria-pressed={origemFiltro === "salao"}
              title={
                origemFiltro === "salao"
                  ? "Mostrando só o salão — clique para ver todos"
                  : "Filtrar pedidos do salão (FoodFly)"
              }
            >
              <Utensils size={16} aria-hidden />
              <span>Salão</span>
            </button>
            <button
              type="button"
              onClick={() =>
                setOrigemFiltro((prev) =>
                  prev === "cardapio" ? "todos" : "cardapio",
                )
              }
              className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-primary ${
                origemFiltro === "cardapio"
                  ? "bg-primary text-white shadow-sm"
                  : "text-neutral-600 hover:bg-white hover:text-neutral-900"
              }`}
              aria-pressed={origemFiltro === "cardapio"}
              title={
                origemFiltro === "cardapio"
                  ? "Mostrando só o cardápio — clique para ver todos"
                  : "Filtrar pedidos do Cardápio Web"
              }
            >
              <BookOpen size={16} aria-hidden />
              <span>Cardápio</span>
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {isOffline && (
            <span className="rounded-md border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700">
              Sem conexao - aguardando retorno da internet
            </span>
          )}
          {showLojaSelector && lojas.length >= 1 && (
            <div className="flex min-w-0 max-w-full items-center gap-2">
              <Store
                size={18}
                className="shrink-0 text-neutral-500"
                aria-hidden
              />
              <ReactSelect<LojaOption>
                inputId="painel-loja-select"
                value={selectedLojaOption}
                onChange={(option: SingleValue<LojaOption>) => {
                  setSelectedLojaId(option?.value ?? undefined);
                }}
                options={lojaOptions}
                isClearable
                placeholder="Selecione a loja"
                className="min-w-[220px] flex-1 sm:min-w-[260px] sm:max-w-md sm:flex-none"
                styles={SELECT_STYLES}
              />
            </div>
          )}
          <div className="flex h-[38px] min-h-[38px] min-w-0 max-w-full items-center gap-2 rounded-md border border-neutral-300 bg-white px-2.5 sm:max-w-xs">
            <Search
              size={18}
              className="shrink-0 text-neutral-500"
              aria-hidden
            />
            <input
              type="search"
              value={pedidoBusca}
              onChange={(e) => setPedidoBusca(e.target.value)}
              placeholder="Nº do pedido"
              className="h-full min-w-0 flex-1 border-0 bg-transparent text-sm text-neutral-800 outline-none placeholder:text-neutral-400"
              aria-label="Buscar pedido por display"
            />
          </div>
          <button
            type="button"
            onClick={() => fetchPainel()}
            disabled={refreshing}
            className="rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-700 shadow-sm hover:bg-neutral-50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-70"
          >
            {refreshing ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={16} className="animate-spin" />
                Atualizando...
              </span>
            ) : (
              "Atualizar"
            )}
          </button>
        </div>
      </header>
      <div className="min-h-0 flex-1 overflow-hidden">
        <PainelKanban
          data={data}
          pedidoBusca={pedidoBusca}
          origemFiltro={origemFiltro}
          prioridadesMontagem={prioridadesMontagem}
          priorizandoPrioridadeKey={priorizandoPrioridadeKey}
          removendoPrioridadeKey={removendoPrioridadeKey}
          showPriorizarAction={canPriorizarMontagem}
          onPriorizarMontagem={priorizarMontagem}
          onRemoverPrioridadeMontagem={removerPrioridadeMontagem}
          onRefetchPainel={fetchPainel}
          lojaId={selectedLojaId}
          finalizadosModalOpen={finalizadosModalOpen}
          onCloseFinalizadosModal={closeFinalizadosModal}
        />
      </div>
      <PedidosAtrasadosModal
        isOpen={atrasadosModalOpen}
        onClose={() => setAtrasadosModalOpen(false)}
        itens={pedidosAtrasados}
      />
      <BordasEmFaltaModal
        isOpen={bordasModalOpen}
        onClose={() => setBordasModalOpen(false)}
        itens={aguardandoBorda}
        nowMs={nowMs}
      />
    </main>
  );
};

const Painel: React.FC = () => {
  return (
    <PainelProvider>
      <PainelContent />
    </PainelProvider>
  );
};

export default Painel;
